import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { fetchConsultationById, updateConsultation, deleteConsultation } from "../../redux/slices/consultationSlice";
import { FiEdit3 } from "react-icons/fi";
import DeleteModal from "../../components/common/DeleteModal";
import { toast } from "react-toastify";

export default function AppointmentEdit() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { detail: appointment, detailStatus } = useSelector((state) => state.consultations);

  const [formData, setFormData] = useState({
    scheduled_at: "",
    status: "",
    notes: ""
  });
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (id) dispatch(fetchConsultationById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (appointment) {
      setFormData({
        scheduled_at: appointment.scheduled_at ? new Date(appointment.scheduled_at).toISOString().slice(0, 16) : "",
        status: appointment.status || "",
        notes: appointment.notes || ""
      });
    }
  }, [appointment]);

  if (detailStatus === "loading") return <div className="px-20 pt-10">Loading...</div>;
  if (detailStatus === "failed") return <div className="px-20 pt-10">Failed to load appointment data.</div>;
  if (!appointment) return <div className="px-20 pt-10">Appointment not found.</div>;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    console.log('AppointmentEdit: Saving appointment data:', {
      id,
      formData
    });

    try {
      // Create FormData for backend (as expected by Postman example)
      const formDataToSend = new FormData();

      // Add appointment fields in bracket notation as expected by backend
      if (formData.scheduled_at) formDataToSend.append('appointment[scheduled_at]', formData.scheduled_at);
      if (formData.status) formDataToSend.append('appointment[status]', formData.status);
      if (formData.notes) formDataToSend.append('appointment[notes]', formData.notes);

      console.log('AppointmentEdit: FormData to send to backend:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}: ${value}`);
      }

      // Update appointment
      await dispatch(updateConsultation({ id, data: formDataToSend })).unwrap();

      toast.success('Appointment updated successfully!');
      navigate(-1);
    } catch (error) {
      console.error('AppointmentEdit: Save failed:', error);
      const errorMessage = error?.response?.data?.meta?.message || 'Failed to save appointment. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleDelete = () => {
    dispatch(deleteConsultation(id));
    setModalOpen(false);
    navigate(-2); // Go back after delete
  };

  return (
    <div className="relative min-h-screen bg-white font-inter px-6 pt-12">
      {/* Top bar */}
      <div className="flex justify-between items-start mb-10">
        <div className="flex items-start">
          <button
            onClick={() => navigate(-1)}
            aria-label="Go Back"
            className="mt-1 mr-3 p-2 text-2xl text-gray-800 hover:text-blue-600"
          >
            <MdOutlineArrowBackIos />
          </button>
          <div>
            <div className="text-xl font-bold text-black">
              Edit Appointment: {appointment.pet?.name || 'N/A'}
            </div>
            <div className="text-gray-600 text-sm mt-1">Update appointment information</div>
          </div>
        </div>

        <div className="flex gap-3">
          {/* Delete Button */}
          <button
            onClick={() => setModalOpen(true)}
            className="p-2 text-gray-600 hover:text-red-500"
          >
            <FiTrash2 size={20} />
          </button>

          <button
            onClick={() => navigate(-1)}
            className="border border-gray-300 rounded-lg px-4 py-2 text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-[#347DFF] text-white rounded-lg px-5 py-2 text-base font-medium hover:bg-blue-600 transition"
          >
            Save Changes
          </button>
        </div>
      </div>

      {/* Form Sections */}
      <div className="space-y-10 px-10">
        {/* Appointment Information */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">
              <FiEdit3 size={22} />
            </span>{" "}
            Appointment Information
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-1">Scheduled Date & Time</label>
              <input
                type="datetime-local"
                name="scheduled_at"
                value={formData.scheduled_at}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select Status</option>
                <option value="upcoming">Upcoming</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Pet Parent</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                {[appointment.booker?.first_name, appointment.booker?.last_name].filter(Boolean).join(' ') || 'N/A'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Pet</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                {appointment.pet?.name || 'N/A'} ({appointment.pet?.breed || 'N/A'})
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Veterinarian</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                {[appointment.provider?.first_name, appointment.provider?.last_name].filter(Boolean).join(' ') || 'N/A'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Service Provider</label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                {appointment.service_provider?.name || 'N/A'}
              </div>
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div>
          <div className="flex items-center gap-1 text-gray-700 font-medium mb-5">
            <span className="rounded p-1">📝</span> Notes
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Appointment Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="6"
              className="w-full rounded-lg px-3 py-2 bg-gray-100 border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
              placeholder="Enter appointment notes..."
            />
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <DeleteModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onDelete={handleDelete}
        profileType="Appointment"
      />
    </div>
  );
}
